import styled from 'styled-components';
import VariableRequest from '@requests/global_variables';

import { useEffect, useState } from 'react';
import { GlobalVariable } from './types';
import { GridActionsCellItem, GridValueFormatterParams } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Stack } from '@mui/material';
import { FlowGlobalVariableModalVariant, State, flowActions } from '@store/index';
import { Button } from '@common/Button';
import { Table } from '@common/Table';
import { LayoutWorkspace } from '@common/layout';
import { ServiceNavigation } from '@components/Workspace/Navigation';
import { ResourceFallback } from '@common/ResourceFallback';
import { copyToClipboard } from '@common/index';
import { Loader } from '@common/hooks/useLoader';
import { RequestError } from '@requests/common';

import {
  AddOutlined,
  CheckCircleOutline,
  ContentCopyOutlined,
  DeleteOutlineOutlined,
  EditOutlined,
  DataObjectOutlined,
} from '@mui/icons-material';

const LayoutContainer = styled.div`
  display: flex;
  align-items: flex-start;
  height: calc(100vh - 61px);
`;

const CenterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  // full width - left navbar - tabs sidebar
  width: calc(100vw - 61px - 288px);
  margin-top: 3rem;
`;

export function GlobalVariables() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const { service_id } = useParams();

  const services = useSelector((state: State) => state.flow.services);
  const service = services.find((service) => service.service_id === service_id);

  const [copied, setCopied] = useState<Map<string, NodeJS.Timeout>>(new Map());

  const {
    data: rows = [],
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery<GlobalVariable[], RequestError, GlobalVariable[], [string, string | undefined]>({
    queryKey: ['globalVariables', service_id],
    queryFn: VariableRequest.listVariables,
    refetchOnReconnect: true,
    enabled: Boolean(service_id),
    retry: false,
  });

  useEffect(() => {
    return () => {
      for (const timeout of copied.values()) {
        clearTimeout(timeout);
      }
    };
  }, []);

  const openReadEditModal =
    (type: FlowGlobalVariableModalVariant.READ | FlowGlobalVariableModalVariant.EDIT) => (id: string) => {
      const data = rows.find((row) => row.id === id);
      if (!data) return;
      dispatch(
        flowActions.setFlowGlobalVariableModal({
          type,
          data,
        })
      );
    };

  const openDeleteModal = (id: string) => {
    const row = rows.find((row) => row.id === id);
    if (!row) return;
    dispatch(
      flowActions.setFlowGlobalVariableModal({
        type: FlowGlobalVariableModalVariant.DELETE,
        data: { id, key: row.key },
      })
    );
  };

  const openCreateModal = () =>
    dispatch(flowActions.setFlowGlobalVariableModal({ type: FlowGlobalVariableModalVariant.CREATE }));

  const handleCopyValue = async (id: string) => {
    if (copied.has(id)) return;

    const row = rows.find((row) => row.id === id);
    if (!row) {
      enqueueSnackbar('Failed to find secret!', { variant: 'error', autoHideDuration: 3000 });
      return;
    }

    try {
      copyToClipboard(row.value);
      enqueueSnackbar(`${row.key}, copied to clipboard!`, { variant: 'success', autoHideDuration: 3000 });

      const deleteCopied = (id: string) => {
        setCopied((prev) => {
          const newMap = new Map(prev);
          newMap.delete(id);
          return newMap;
        });
      };

      setCopied((prev) => {
        const newMap = new Map(prev);
        const timeout = setTimeout(() => deleteCopied(id), 3000);
        newMap.set(id, timeout);
        return newMap;
      });
    } catch (err) {
      enqueueSnackbar('Failed to copy to clipboard!', { variant: 'error', autoHideDuration: 3000 });
    }
  };

  const isEmptyGlobalVariables = rows.length === 0;

  const columns = [
    {
      field: 'key',
      headerName: `${rows.length} Global Variables Available`,
      flex: 120,
      sortable: true,
      renderCell: (params: GridValueFormatterParams<string>) => (
        <Stack direction='row' alignItems='center' spacing={2}>
          <DataObjectOutlined sx={{ fontSize: '18px' }} />
          <span>{params.value}</span>
        </Stack>
      ),
    },
    {
      field: 'value',
      headerName: 'Value',
      flex: 400,
      sortable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 100,
      cellClassName: 'actions',
      getActions: ({ id }: { id: string }) => {
        return [
          <GridActionsCellItem
            key='copy'
            disabled={copied.has(id)}
            icon={copied.has(id) ? <CheckCircleOutline /> : <ContentCopyOutlined />}
            label='Copy'
            color='inherit'
            onClick={() => handleCopyValue(id)}
          />,
          <GridActionsCellItem
            key='edit'
            icon={<EditOutlined />}
            label='Edit'
            color='inherit'
            onClick={() => openReadEditModal(FlowGlobalVariableModalVariant.EDIT)(id)}
          />,
          <GridActionsCellItem
            key='delete'
            icon={<DeleteOutlineOutlined />}
            label='Delete'
            color='inherit'
            onClick={() => openDeleteModal(id)}
          />,
        ];
      },
    },
  ];

  const empty = (
    <LayoutContainer>
      <ServiceNavigation />
      <ResourceFallback
        resourceName='Global Variable'
        resourceIcon={<DataObjectOutlined />}
        resourceInfo='Global Variables are key-value pairs that can be used in all workflows within this workspace. Updating a variable will propegate the changes to all workflows that use it.'
        onClickCreate={openCreateModal}
        onClickGuide={() => navigate(`/documentation/global_variables`)}
        createDisabled={false}
      />
    </LayoutContainer>
  );

  const requestError = (
    <LayoutContainer>
      <ServiceNavigation />
      <CenterContainer>
        <Alert
          sx={{ width: '80%' }}
          severity='error'
          action={
            <Button customColor='red' onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          {error?.response?.data?.error || error?.message}
        </Alert>
      </CenterContainer>
    </LayoutContainer>
  );

  const ModalBody = () => {
    if (error) return requestError;

    if (isFetching && !rows.length) {
      return (
        <LayoutContainer>
          <ServiceNavigation />
          <CenterContainer>
            <Loader />
          </CenterContainer>
        </LayoutContainer>
      );
    }

    if (isEmptyGlobalVariables) return empty;

    return (
      <LayoutContainer>
        <ServiceNavigation />
        <div style={{ width: 'calc(100vw - 240px - 61px - 3rem)' }}>
          <Table
            width='calc(100vw - 240px - 61px - 3rem)'
            height='calc(100vh - 61px)'
            data={rows}
            columns={columns}
            loading={isLoading || isFetching}
            density={'standard'}
            sortModel='date_created'
            getRowId={(row) => row.id}
            onRowClick={({ row }) => openReadEditModal(FlowGlobalVariableModalVariant.READ)(row.id)}
            onRowDoubleClick={({ row }) => {
              handleCopyValue(row.id);
            }}
          />
        </div>
      </LayoutContainer>
    );
  };

  return (
    <LayoutWorkspace
      header={service?.service_name || 'Workspace'}
      body={<ModalBody />}
      actions={
        <Stack direction='row' spacing={1.5}>
          <Button
            customColor='black'
            startIcon={<AddOutlined sx={{ fontSize: '2.2ch !important' }} />}
            onClick={openCreateModal}
          >
            Add Variable
          </Button>
        </Stack>
      }
    />
  );
}
